// `slide-finisher`: the two lines the talk ends on (user story 11, presentation polish 2026-08-11).
//
// **Four states since 2026-08-13** (`FINISHER_MAX_REVEAL` 3): the slide arrives as the empty circle,
// "Express your intent." lands on the first click, "Make your impact." on the second, and the
// circular Festival mark covers both on the third. The lines used to be on screen from step 0, which
// spent the whole ending on one beat; the empty circle is the pause the presenter speaks into.
//
// Both lines' boxes are reserved from step 0 and revealed by opacity rather than by mounting, so the
// first line does not sit dead-centre and then jump when the second arrives (user story 7).

import type { ReactElement } from 'react';
import { SlideFrame } from './SlideFrame';
import { FINISHER_LINES, FINISHER_MAX_REVEAL } from './content';
import { FESTIVAL_ASSETS } from '../festival-assets';
import './slides.css';

const FINISHER_FONT_SIZE = 92;

function Line({ text, accent, shown }: { text: string; accent: boolean; shown: boolean }): ReactElement {
  return (
    <div
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: FINISHER_FONT_SIZE,
        color: accent ? 'var(--deck-pink-lava)' : 'var(--deck-card)',
        lineHeight: 'var(--leading-tight)',
        letterSpacing: '-0.02em',
        opacity: shown ? 1 : 0,
        transition: 'opacity 450ms ease-out',
      }}
    >
      {text}
    </div>
  );
}

export function FinisherSlide({ revealStep }: { revealStep: number }): ReactElement {
  const step = Math.max(0, Math.min(revealStep, FINISHER_MAX_REVEAL));
  const [first, second] = FINISHER_LINES;
  return (
    <SlideFrame center festivalBackdrop>
      <div
        data-testid="festival-finisher-circle"
        style={{
          position: 'relative',
          height: 972,
          aspectRatio: '1',
          borderRadius: '50%',
          background: 'var(--deck-circle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          data-testid="finisher-copy"
          style={{
            opacity: step >= FINISHER_MAX_REVEAL ? 0 : 1,
            transition: 'opacity 450ms ease-out',
            textAlign: 'center',
          }}
        >
          <div data-testid="reveal-finisher-intent">
            <Line text={first!} accent={false} shown={step >= 1} />
          </div>
          <div data-testid="reveal-finisher-impact">
            <Line text={second!} accent shown={step >= 2} />
          </div>
        </div>
        {step >= FINISHER_MAX_REVEAL && (
          <div
            data-testid="finisher-signoff"
            className="demo-reveal"
            style={{ position: 'absolute', width: '72%', color: 'var(--deck-card)', textAlign: 'center' }}
          >
            <img
              data-testid="finisher-festival-logo"
              src={FESTIVAL_ASSETS.logoVertical}
              alt="Northrop Festival"
              style={{ width: '70%', filter: 'brightness(0) invert(1)' }}
            />
            <p style={{ margin: '42px 0 12px', fontSize: 28 }}>With ❤️ from the Digital Innovation Team</p>
            <p style={{ margin: 0, fontSize: 24 }}>Nick, Martin, Mortaza, Sam and Jupiter</p>
          </div>
        )}
      </div>
    </SlideFrame>
  );
}
